import { collection, doc, endBefore, getDoc, getDocs, getFirestore, limit, limitToLast, orderBy, Query, query, QueryDocumentSnapshot, QuerySnapshot, startAfter, startAt, where } from "firebase/firestore";
import employeeConverter from "../converters/employeeConverter";
import vacationRequestConverter from "../converters/vacationRequestConverter";
import { RequestStatus } from "../types/Type";
import { Vacation } from "../types/Vacation";
import { DateTime, Interval } from "luxon";

export interface VacationsQueryResult {
  first: QueryDocumentSnapshot,
  last: QueryDocumentSnapshot,
  results: Vacation[],
  totalCount: number,
}

export const queryVacations = async (
  page: 'initial' | 'forward' | 'backward' | 'same',
  vacationPerPage: number, 
  status: RequestStatus | 'All' = 'New', 
  start: QueryDocumentSnapshot |  null = null, 
  end: QueryDocumentSnapshot | null = null) => {

  const db = getFirestore();
  let q: Query<Vacation> | undefined;

  if (page === 'initial' || (!start && !end)) {
    q = query(collection(db, 'Vacations'), orderBy('dateRequested', 'desc'), limit(vacationPerPage)).withConverter(vacationRequestConverter);
  } else if (page === 'forward') {
    q = query(collection(db, 'Vacations'), orderBy('dateRequested', 'desc'), startAfter(end), limit(vacationPerPage)).withConverter(vacationRequestConverter);
  } else if (page === 'backward') {
    q = query(collection(db, 'Vacations'), orderBy('dateRequested', 'desc'), endBefore(start), limitToLast(vacationPerPage)).withConverter(vacationRequestConverter);
  } else {
    q = query(collection(db, 'Vacations'), orderBy('dateRequested', 'desc'), startAt(start), limit(vacationPerPage)).withConverter(vacationRequestConverter);
  }

  if (q) {
    let totalCount = 0;
    const vConf = (await getDoc(doc(db, `Configs/Vacations`))).data()!;

    if (status !== 'All') {
      q = query(q, where('requestStatus', '==', status));
      totalCount = vConf[status + 'Count'];
    } else {
      totalCount = vConf.TotalCount;
    }
    
    const querySnapshot = await getDocs(q);
    const vqr: VacationsQueryResult = {
      first: querySnapshot.docs[0],
      last: querySnapshot.docs[querySnapshot.docs.length - 1],
      results: querySnapshot.docs.map((d) => d.data()),
      totalCount,
    };

    return vqr;
  }
};

export const queryOngoingApprovedVacations = async () => {
  const db = getFirestore();
  const q: Query<Vacation> = query(collection(db, 'Vacations')).withConverter(vacationRequestConverter);
};

export const queryVacationByIdNumber = async (idNumber: string) => {
  const db = getFirestore();
  // const q = query(collection(db, `Vacations`), where('idNumber', '==', idNumber), orderBy('dateRequested', 'desc')).withConverter(vacationRequestConverter);
  const q = query(collection(db, `Employees/${idNumber}/Vacations`), orderBy('dateRequested', 'desc')).withConverter(vacationRequestConverter);
  return (await getDocs(q)).docs.map((d) => d.data());
};

export const queryLastVacation = async (idNumber: string) => {
  const db = getFirestore();
  const qSnapshot = await getDocs(
    query(
      collection(db, `Employees/${idNumber}/Vacations`),
      where('requestStatus', '==', 'Approved'),
      orderBy('dateEnd', 'desc'),
      limit(1)
    ).withConverter(vacationRequestConverter)
  );

  if (qSnapshot.docs.length === 1) {
    const v = qSnapshot.docs[0].data();

    let i = 1;
    let start = DateTime.fromFormat(v.dateEnd, 'yyyyMMdd');
    let end = DateTime.utc();
    if (start > end) {
      const tmp = start;
      start = end;
      end = tmp;
      i = -1;
    }

    const diff = Interval.fromDateTimes(start, end);
    const years = diff.length('years') * i;

    return {
      vacation: v,
      years,
      eligible: years > 2,
    };
  }

  return {
    vacation: undefined,
    years: undefined,
    eligible: false,
  };
};